/**
 * Project Detail Page
 * Full property information with image gallery and lightbox.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Container, Section, Spacer } from '../components/primitives';
import { Headline, Title, Body, Label } from '../components/primitives/Typography';
import { PageLoader } from '../components/common/LoadingSpinner';
import Button from '../components/common/Button';
import ImageLightbox from '../components/common/ImageLightbox';
import {
  formatPriceRange,
  formatArea,
  formatBedrooms,
  formatPossessionDate,
  getProjectTypeLabel,
} from '../lib/utils';
import type { Project } from '../types';

interface ImageItem {
  url: string;
  alt: string;
}

export default function ProjectDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    if (slug) {
      loadProject();
    }
  }, [slug]);

  async function loadProject() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.getProject(slug!);
      setProject(res.project);
    } catch {
      setError('Project not found');
    } finally {
      setLoading(false);
    }
  }

  // Process images
  const processImages = (project: Project): ImageItem[] => {
    if (!project.images || project.images.length === 0) return [];

    const heroImage = project.images.find((img) => img.image_type === 'hero');
    const allImages = [
      ...(heroImage ? [heroImage] : []),
      ...project.images.filter((img) => img.image_type === 'gallery'),
      ...project.images.filter((img) => img.image_type === 'floorplan'),
      ...project.images.filter((img) => img.image_type === 'amenity'),
    ];

    return allImages.map((img) => ({
      url: img.image_url,
      alt: img.alt_text || project.title,
    }));
  };

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  if (loading) {
    return <PageLoader />;
  }

  if (error || !project) {
    return (
      <main>
        <Section className="pt-32">
          <Container narrow>
            <div className="text-center py-16">
              <h1 className="text-h2 font-medium text-text-primary">Project not found</h1>
              <p className="mt-4 text-text-secondary">
                The property you're looking for doesn't exist.
              </p>
              <Link
                to="/real-estate"
                className="mt-8 inline-block text-accent hover:underline"
              >
                Back to properties
              </Link>
            </div>
          </Container>
        </Section>
      </main>
    );
  }

  const images = processImages(project);
  const heroImage = images[0];
  const thumbnails = images.slice(1, 7);
  const remainingCount = images.length - 7;

  return (
    <main>
      {/* Back Navigation */}
      <Section size="sm" className="pt-20 md:pt-28 pb-6">
        <Container>
          <Link
            to="/real-estate"
            className="inline-flex items-center text-small text-text-tertiary hover:text-text-primary"
          >
            <span className="mr-2">&larr;</span> Back to properties
          </Link>
        </Container>
      </Section>

      {/* Image Gallery */}
      <Section size="sm" className="py-0">
        <Container>
          {images.length > 0 ? (
            <div className="space-y-2">
              {/* Hero Image */}
              <button
                onClick={() => openLightbox(0)}
                className="w-full aspect-[16/9] bg-surface-subtle overflow-hidden cursor-pointer group"
              >
                <img
                  src={heroImage.url}
                  alt={heroImage.alt}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
              </button>

              {/* Thumbnail Grid */}
              {thumbnails.length > 0 && (
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {thumbnails.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => openLightbox(index + 1)}
                      className="relative aspect-square bg-surface-subtle overflow-hidden cursor-pointer group"
                    >
                      <img
                        src={image.url}
                        alt={image.alt}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      {/* Show +N overlay on last thumbnail if more images exist */}
                      {index === thumbnails.length - 1 && remainingCount > 0 && (
                        <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                          <span className="text-white text-lg font-medium">
                            +{remainingCount} more
                          </span>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* No images placeholder */
            <div className="aspect-[16/9] bg-surface-subtle flex items-center justify-center">
              <div className="text-center">
                <div className="w-24 h-24 bg-surface-muted flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-medium text-text-tertiary">
                    {project.title
                      .split(' ')
                      .map((w) => w[0])
                      .slice(0, 2)
                      .join('')
                      .toUpperCase()}
                  </span>
                </div>
                <p className="text-text-tertiary text-small">No images available</p>
              </div>
            </div>
          )}
        </Container>
      </Section>

      {/* Project Header */}
      <Section size="sm">
        <Container>
          <div className="flex items-center gap-3 mb-4">
            <span className="px-3 py-1 text-caption font-medium uppercase tracking-wider text-white bg-text-primary">
              {getProjectTypeLabel(project.project_type)}
            </span>
            <span className="text-small text-text-tertiary">{project.city}</span>
          </div>

          <Headline>{project.title}</Headline>

          <p className="mt-3 text-text-secondary flex items-center gap-2">
            <svg
              className="w-4 h-4 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {project.location}
          </p>

          <p className="mt-4 text-h3 font-medium text-text-primary">
            {formatPriceRange(project.price_min, project.price_max, project.price_unit)}
          </p>
        </Container>
      </Section>

      {/* Key Specs */}
      <Section size="sm" background="subtle">
        <Container>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {(project.bedrooms_min || project.bedrooms_max) && (
              <div>
                <Label>Bedrooms</Label>
                <p className="mt-1 text-lg font-medium text-text-primary">
                  {formatBedrooms(project.bedrooms_min, project.bedrooms_max)}
                </p>
              </div>
            )}

            {(project.area_min || project.area_max) && (
              <div>
                <Label>Area</Label>
                <p className="mt-1 text-lg font-medium text-text-primary">
                  {formatArea(project.area_min, project.area_max, project.area_unit)}
                </p>
              </div>
            )}

            <div>
              <Label>Possession</Label>
              <p className="mt-1 text-lg font-medium text-text-primary">
                {formatPossessionDate(project.possession_date)}
              </p>
            </div>

            {project.rera_id && (
              <div>
                <Label>RERA ID</Label>
                <p className="mt-1 text-lg font-medium text-text-primary break-all">
                  {project.rera_id}
                </p>
              </div>
            )}
          </div>
        </Container>
      </Section>

      {/* Description */}
      {project.description && (
        <Section size="sm">
          <Container narrow>
            <Title as="h2" className="mb-6">
              About This Property
            </Title>
            <Body className="whitespace-pre-line">{project.description}</Body>
          </Container>
        </Section>
      )}

      {/* Amenities */}
      {project.amenities && project.amenities.length > 0 && (
        <Section size="sm">
          <Container>
            <Title as="h2" className="mb-6">
              Amenities
            </Title>
            <div className="flex flex-wrap gap-2">
              {project.amenities.map((amenity, index) => (
                <span
                  key={index}
                  className="px-4 py-2 text-small text-text-secondary bg-surface-subtle border border-border-subtle"
                >
                  {amenity}
                </span>
              ))}
            </div>
          </Container>
        </Section>
      )}

      {/* Highlights */}
      {project.highlights && project.highlights.length > 0 && (
        <Section size="sm" background="subtle">
          <Container>
            <Title as="h2" className="mb-6">
              Highlights
            </Title>
            <ul className="grid md:grid-cols-2 gap-3">
              {project.highlights.map((highlight, index) => (
                <li key={index} className="flex items-start gap-3">
                  <svg
                    className="w-5 h-5 text-accent flex-shrink-0 mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  <span className="text-text-secondary">{highlight}</span>
                </li>
              ))}
            </ul>
          </Container>
        </Section>
      )}

      {/* Builder Info */}
      {project.builder_name && (
        <Section size="sm">
          <Container narrow>
            <Title as="h2" className="mb-6">
              Developer Information
            </Title>
            <div className="space-y-4">
              <div>
                <Label>Developer</Label>
                <p className="mt-1 text-lg text-text-primary">{project.builder_name}</p>
              </div>
              {project.rera_id && (
                <div>
                  <Label>RERA Registration</Label>
                  <p className="mt-1 text-text-secondary">{project.rera_id}</p>
                </div>
              )}
            </div>
          </Container>
        </Section>
      )}

      {/* CTA Section */}
      <Section background="muted">
        <Container narrow>
          <div className="text-center">
            <Title as="h2" className="mb-4">
              Interested in this property?
            </Title>
            <Body className="mb-8">
              Schedule a consultation with our experts to learn more about this project and
              explore your options.
            </Body>
            <Link to="/book">
              <Button size="lg">Book a Consultation</Button>
            </Link>
          </div>
        </Container>
      </Section>

      <Spacer size="lg" />

      {/* Lightbox */}
      <ImageLightbox
        images={images}
        isOpen={lightboxOpen}
        currentIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
        onNavigate={setLightboxIndex}
      />
    </main>
  );
}
